import { useApolloClient } from "@apollo/client";
import { omit } from "lodash";
import { gql } from "__generated__/gql";
import {
  SkuComponentType,
  UpcValidationInputRecordInput,
} from "__generated__/gql/graphql";
import {
  assertPresence,
  getAssertedArrayOfType,
  makeAssertIsEnum,
} from "utils/assertions";
import skuComponentSchema from "utils/schemas/skuComponent";

export const SKU_COMPONENT_TYPES = [
  SkuComponentType.UNIT,
  SkuComponentType.INNER,
  SkuComponentType.CASE,
] as const;

export const SKU_COMPONENT_TYPE_LABELS: Record<SkuComponentType, string> = {
  [SkuComponentType.UNIT]: "Unit",
  [SkuComponentType.INNER]: "Inner Case",
  [SkuComponentType.CASE]: "Case",
};

export const SKU_COMPONENT_TYPE_LABELS_SHORT: Record<SkuComponentType, string> =
  {
    ...SKU_COMPONENT_TYPE_LABELS,
    [SkuComponentType.INNER]: "Inner",
  };

export function getInitialSkuComponents() {
  return SKU_COMPONENT_TYPES.map((skuComponentType) =>
    skuComponentSchema.cast({ skuComponentType }),
  );
}

export function getArrayOfSkuComponentTypes(skuComponentTypes: unknown) {
  if (!skuComponentTypes) return;
  return getAssertedArrayOfType(
    skuComponentTypes,
    makeAssertIsEnum<SkuComponentType>(SkuComponentType),
  );
}

const validateUpcUniquenessQuery = gql(/* GraphQL */ `
  query ValidateUpcUniqueness($validations: [UpcValidationInputRecordInput!]!) {
    validateUpcUniqueness(validations: $validations) {
      inputIndex
      upc
      ignoreSkuId
      isUnique
    }
  }
`);

export function useValidateUpcUniqueness() {
  const client = useApolloClient();
  return async function validateUpcUniqueness(
    validations: UpcValidationInputRecordInput[],
  ) {
    const { data } = await client.query({
      query: validateUpcUniquenessQuery,
      variables: { validations },
      fetchPolicy: "no-cache",
    });
    return data.validateUpcUniqueness.map((result) => {
      assertPresence(result);
      return omit(result, "__typename");
    });
  };
}
